import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { LogOut, Plus, RefreshCw, SlidersHorizontal, Search } from 'lucide-react';
import { useApi } from '../api/use-api';
import { listHosts, revokeHost, type HostSummary } from '../api/hosts';
import { logout } from '../api/auth';
import { useAuth } from '../auth/context';
import { HostCard } from '../components/HostCard';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Field } from '../components/Field';
import { Input } from '../components/Input';
import { Popover } from '../components/Popover';
import { Select } from '../components/Select';
import { ShortcutOverlay } from '../components/ShortcutOverlay';
import { Spinner } from '../components/Spinner';
import { CreateHostDialog } from '../components/CreateHostDialog';
import { useToast } from '../components/Toast';
import { useGridNavigation } from '../hooks/use-grid-navigation';
import { useHotkeys } from '../hooks/use-hotkeys';
import {
  readEncodingPref, writeEncodingPref,
  readResolutionPref, writeResolutionPref,
  readCodecPref, writeCodecPref,
  readFpsPref, writeFpsPref,
  readJpegQualityPref, writeJpegQualityPref,
  FPS_CHOICES, JPEG_QUALITY_CHOICES,
} from '../session/encoding-pref';
import { webCodecsSupported } from '../session/h264-decoder';
import type { Codec, EncodingMode, Resolution } from '../api/sessions';

export function HostListPage() {
  const api = useApi();
  const auth = useAuth();
  const navigate = useNavigate();
  const { push } = useToast();
  const [hosts, setHosts] = useState<HostSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [encoding, setEncoding] = useState<EncodingMode>(() => readEncodingPref());
  const [resolution, setResolution] = useState<Resolution>(() => readResolutionPref());
  // If a stored pref is 'h264' on a browser that has since lost WebCodecs support, fall back to 'jpeg'.
  const [codec, setCodec] = useState<Codec>(() => {
    const pref = readCodecPref();
    return pref === 'h264' && !webCodecsSupported ? 'jpeg' : pref;
  });
  const [fps, setFps] = useState<number>(() => readFpsPref());
  const [jpegQuality, setJpegQuality] = useState<number>(() => readJpegQualityPref());
  const [showCreate, setShowCreate] = useState(false);
  const [showDefaults, setShowDefaults] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [filter, setFilter] = useState('');

  const filterRef = useRef<HTMLInputElement>(null);
  const gridRef = useGridNavigation<HTMLDivElement>();

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await listHosts(api);
      setHosts(res.hosts);
    } catch {
      setError('Could not load hosts.');
    }
  }, [api]);

  useEffect(() => { void refresh(); }, [refresh]);

  useHotkeys({
    'g h': () => void navigate('/hosts'),
    c: () => setShowCreate(true),
    r: () => void refresh(),
    '/': (e) => { e.preventDefault(); filterRef.current?.focus(); },
    '?': () => setShowShortcuts(true),
  }, { enabled: true, ignoreWhenTyping: true });

  async function onLogout() {
    try { await logout(api); } catch { /* swallow */ }
    auth.logout();
    void navigate('/login', { replace: true });
  }

  function onConnect(host: HostSummary) {
    void navigate(`/session/new?hostId=${encodeURIComponent(host.id)}`);
  }

  async function onDelete(host: HostSummary) {
    try {
      await revokeHost(api, host.id);
      await refresh();
    } catch (err) {
      push('error', "Couldn't delete host.");
      throw err; // let HostCard show its inline error state
    }
  }

  const filteredHosts = useMemo(() => {
    if (!hosts) return null;
    const q = filter.trim().toLowerCase();
    if (!q) return hosts;
    return hosts.filter((h) => h.name.toLowerCase().includes(q));
  }, [hosts, filter]);

  // 'row' lays the controls out horizontally for the desktop nav-bar cluster;
  // 'stack' keeps them vertical for the mobile Defaults popover.
  const renderDefaults = (layout: 'row' | 'stack') => {
    const fieldClass = layout === 'row' ? 'w-32' : '';
    return (
      <div className={layout === 'row' ? 'flex items-end gap-3' : 'space-y-3 p-1'}>
        <Field label="Encoding" className={fieldClass}>
          <Select
            value={encoding}
            onChange={(e) => { const next = e.target.value as EncodingMode; setEncoding(next); writeEncodingPref(next); }}
          >
            <option value="full">Full</option>
            <option value="skip-static">Skip static</option>
          </Select>
        </Field>
        <Field label="Quality" className={fieldClass}>
          <Select
            value={resolution}
            onChange={(e) => { const next = e.target.value as Resolution; setResolution(next); writeResolutionPref(next); }}
          >
            <option value="native">Native</option>
            <option value="720p">720p</option>
            <option value="540p">540p</option>
            <option value="360p">360p</option>
          </Select>
        </Field>
        <Field label="Codec" className={fieldClass}>
          <Select
            value={codec}
            onChange={(e) => { const next = e.target.value as Codec; setCodec(next); writeCodecPref(next); }}
          >
            <option value="jpeg">JPEG</option>
            <option value="h264" disabled={!webCodecsSupported}>H.264</option>
          </Select>
        </Field>
        <Field label="FPS" className={fieldClass}>
          <Select
            value={fps}
            onChange={(e) => { const next = Number.parseInt(e.target.value, 10); setFps(next); writeFpsPref(next); }}
          >
            {FPS_CHOICES.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </Field>
        {codec === 'jpeg' ? (
          <Field label="JPEG q" className={fieldClass}>
            <Select
              value={jpegQuality}
              onChange={(e) => { const next = Number.parseInt(e.target.value, 10); setJpegQuality(next); writeJpegQualityPref(next); }}
            >
              {JPEG_QUALITY_CHOICES.map((v) => <option key={v} value={v}>{v}</option>)}
            </Select>
          </Field>
        ) : null}
      </div>
    );
  };

  return (
    <main className="min-h-screen">
      <header className="border-b border-border-subtle bg-bg-raised/40 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
          <h1 className="text-lg font-semibold tracking-tight">
            <span className="font-[var(--font-serif)] italic text-accent">▸ </span>
            Hosts
          </h1>
          <div className="flex-1" />

          <Card variant="outlined" className="hidden p-2 lg:block">
            {renderDefaults('row')}
          </Card>
          <Button variant="secondary" size="sm" onClick={() => setShowDefaults(true)} className="lg:hidden" aria-label="Defaults">
            <SlidersHorizontal className="h-4 w-4" />
            Defaults
          </Button>

          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" />
            New host
          </Button>
          <Button variant="secondary" size="sm" onClick={() => { void refresh(); }} aria-label="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="ghost" onClick={() => { void onLogout(); }}>
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-8">
        {hosts && hosts.length > 0 && (
          <div className="mb-6 max-w-sm">
            <span className="relative block">
              <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <Input
                ref={filterRef}
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="filter hosts… (press /)"
                className="pl-9"
              />
            </span>
          </div>
        )}

        {hosts === null && !error && (
          <div className="grid place-items-center py-16"><Spinner className="h-6 w-6 text-text-muted" /></div>
        )}
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        {hosts && hosts.length === 0 && (
          <EmptyHosts onCreate={() => setShowCreate(true)} />
        )}
        {hosts && hosts.length > 0 && filteredHosts && filteredHosts.length === 0 && (
          <p className="text-sm text-text-muted">No hosts match <span className="font-mono text-text-primary">{filter}</span>.</p>
        )}
        {filteredHosts && filteredHosts.length > 0 && (
          <div ref={gridRef} role="list" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredHosts.map((h) => (
              <HostCard key={h.id} host={h} onConnect={onConnect} onDelete={onDelete} />
            ))}
          </div>
        )}
      </section>

      <Popover open={showDefaults} onClose={() => setShowDefaults(false)} align="end">
        <h2 className="mb-2 font-mono text-[10px] font-medium uppercase tracking-[0.15em] text-text-muted">Defaults</h2>
        {renderDefaults('stack')}
      </Popover>

      <ShortcutOverlay open={showShortcuts} onClose={() => setShowShortcuts(false)} />

      {showCreate && (
        <CreateHostDialog
          api={api}
          onClose={() => setShowCreate(false)}
          onCreated={() => { void refresh(); }}
        />
      )}
    </main>
  );
}

function EmptyHosts({ onCreate }: { onCreate: () => void }) {
  return (
    <Card variant="outlined" className="space-y-4 px-8 py-12 text-center">
      <p className="font-[var(--font-serif)] text-2xl italic text-text-secondary">
        <span className="not-italic font-sans text-accent">▸ </span>
        no hosts yet
      </p>
      <p className="mx-auto max-w-sm text-sm text-text-secondary">
        Register a host below, then run the host agent on that machine with the printed token. Once it connects, it'll appear here as <span className="text-accent">online</span>.
      </p>
      <div className="flex justify-center pt-2">
        <Button onClick={onCreate}>
          <Plus className="h-4 w-4" />
          New host
        </Button>
      </div>
    </Card>
  );
}
